import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  PlayCircle,
  Mic,
  Sparkles,
  ArrowRight,
  Briefcase,
} from "lucide-react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";

// Öne Çıkan Firmalar
const FeaturedBusinesses = ({ setCurrentPage }) => {
  const [businesses, setBusinesses] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(
      collection(db, "firmalar"),
      where("isVitrin", "==", true),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setBusinesses(
        snap.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            // FirmDetailPage'in beklediği alanlar:
            firma: d.firmaAdi || "",
            logo: d.logoUrl || "",
            logoUrl: d.logoUrl || "",
            sektor: (d.secilenSektorler?.[0]) || d.sektor || "",
            secilenSektorler: d.secilenSektorler || [],
            il: d.sehir || d.il || "",
            ilce: d.ilce || "",
            adres: d.acikAdres || d.adres || "",
            koy: d.sivasKoy || "",
            gsm: d.gsm || "",
            telefon: d.telefon || "",
            email: d.email || "",
            web: d.web || "",
            hakkinda: d.hakkinda || "",
            kurulusYili: d.kurulusYili || "",
            yetkiliKisi: d.yetkiliKisi || "",
            yetkiliPozisyon: d.yetkiliPozisyon || "",
            calismaSaatleri: d.calismaSaatleri || "",
            anahtarKelimeler: d.anahtarKelimeler || [],
            urunler: d.urunler || [],
            isGold: d.isGold || false,
            isVitrin: d.isVitrin || false,
            // Kart görünümü için:
            name: d.firmaAdi || "",
            sehir: d.sehir || "",
          };
        })
      );
    });
    return () => unsub();
  }, []);

  const quickAccessItems = [
    {
      id: "photos",
      page: "photos",
      icon: ImageIcon,
      title: "Resim Sivas",
      subtitle: "Fotoğraf Galerisi",
      image:
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80",
    },
    {
      id: "videos",
      page: "videos",
      icon: PlayCircle,
      title: "Video Sivas",
      subtitle: "Video İçerikler",
      image:
        "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=600&q=80",
    },
    {
      id: "roportaj",
      page: "roportaj",
      icon: Mic,
      title: "Röportaj Sivas",
      subtitle: "Söyleşi & Hikayeler",
      image:
        "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=600&q=80",
    },
    {
      id: "celebrities",
      page: "unlulerSivas",
      icon: Sparkles,
      title: "Ünlüler Sivas",
      subtitle: "Tanınmış İsimler",
      image:
        "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80",
    },
  ];

  const railRef = useRef(null);
  const scrollBy = (px) => {
    if (!railRef.current) return;
    railRef.current.scrollBy({ left: px, behavior: "smooth" });
  };
  const handlePrev = () => scrollBy(-280);
  const handleNext = () => scrollBy(280);

  return (
    <div className="container mx-auto px-4 py-6 md:py-8">
      {/* Hızlı Erişim Kartları */}
      <div className="mb-10">
        <div className="mb-5 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
            Keşfet
          </h2>
          <p className="text-gray-600 text-sm">
            Sivas'ın zengin içeriklerine göz atın
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickAccessItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setCurrentPage(item.page)}
              className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 h-44"
            >
              {/* Arka plan resmi */}
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: `url('${item.image}')` }}
              />

              {/* Karartma katmanı */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 group-hover:from-black/70 transition-all duration-300" />

              {/* İçerik */}
              <div className="relative z-10 h-full flex flex-col justify-between p-4">
                {/* Üst: ikon */}
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center group-hover:bg-white/30 transition-all duration-300">
                  <item.icon className="w-5 h-5 text-white" />
                </div>

                {/* Alt: başlık */}
                <div className="text-left">
                  <div className="text-white/70 text-[10px] font-medium uppercase tracking-widest mb-0.5">
                    {item.subtitle}
                  </div>
                  <div
                    className="text-white font-bold text-base leading-tight"
                    style={{ textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}
                  >
                    {item.title}
                  </div>
                  <div className="flex items-center gap-1 mt-2 text-white/80 text-xs group-hover:text-white transition-colors">
                    <span>Görüntüle</span>
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Vitrindeki Firmalar */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-700 to-slate-800 p-4 md:p-6 shadow-xl">
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="flex items-center font-bold text-white text-lg md:text-2xl mb-1">
              <div className="mr-3 w-8 h-8 md:w-10 md:h-10 rounded-lg bg-amber-400 flex items-center justify-center">
                <Briefcase className="w-5 h-5 md:w-6 md:h-6 text-slate-900" />
              </div>
              VİTRİNDEKİ FİRMALAR
            </h2>
            <p className="text-slate-300 text-xs md:text-sm">
              Öne çıkan işletmeler ve hizmetler
            </p>
          </div>

          <div className="hidden md:flex space-x-2">
            <button
              onClick={handlePrev}
              className="rounded-xl bg-amber-400 hover:bg-amber-500 p-2.5 transition-all hover:scale-105 shadow-lg"
              aria-label="Önceki"
            >
              <ChevronLeft className="h-5 w-5 text-slate-900" />
            </button>
            <button
              onClick={handleNext}
              className="rounded-xl bg-amber-400 hover:bg-amber-500 p-2.5 transition-all hover:scale-105 shadow-lg"
              aria-label="Sonraki"
            >
              <ChevronRight className="h-5 w-5 text-slate-900" />
            </button>
          </div>
        </div>

        {/* Mobil: yatay scroller */}
        <div className="md:hidden -mx-2">
          <div
            ref={railRef}
            className="flex gap-3 px-2 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-px-2"
          >
            {businesses.length === 0 ? (
              <div className="w-full py-8 text-center text-slate-400 text-sm">
                Henüz vitrin firması bulunmuyor.
              </div>
            ) : (
              businesses.map((b) => (
                <button
                  key={b.id}
                  className="snap-start min-w-[210px] max-w-[210px] rounded-xl bg-white shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden"
                  onClick={() => navigate(`/firms/${b.id}`, { state: { firm: b } })}
                >
                  {b.isGold && (
                    <div className="bg-gradient-to-r from-amber-400 to-amber-500 py-1.5 text-center">
                      <span className="text-[10px] font-bold text-slate-900 tracking-wide">
                        ⭐ GOLD FİRMA
                      </span>
                    </div>
                  )}
                  <div className="h-28 flex items-center justify-center bg-white overflow-hidden">
                    {b.logoUrl ? (
                      <img
                        src={b.logoUrl}
                        alt={b.name}
                        className="w-full h-full object-contain p-2"
                        style={{ maxHeight: "100px" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <Briefcase className="w-10 h-10 text-gray-300" />
                      </div>
                    )}
                  </div>

                  <div className="p-3">
                    <h3 className="text-sm font-bold text-gray-900 line-clamp-1">
                      {b.name}
                    </h3>
                    {b.sektor && (
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide mt-1 line-clamp-1">
                        {b.sektor}
                      </p>
                    )}
                    {b.sehir && (
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {b.sehir}
                      </p>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Desktop: grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {businesses.length === 0 ? (
            <div className="col-span-4 py-10 text-center text-slate-400 text-sm">
              Henüz vitrin firması bulunmuyor.
            </div>
          ) : (
            <>
              {businesses.map((b) => (
                <div
                  key={b.id}
                  className="overflow-hidden rounded-xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                  onClick={() => navigate(`/firms/${b.id}`, { state: { firm: b } })}
                >
                  {b.isGold && (
                    <div className="bg-gradient-to-r from-amber-400 to-amber-500 py-2 text-center">
                      <span className="text-xs font-bold text-slate-900 tracking-wide">
                        ⭐ GOLD FİRMA
                      </span>
                    </div>
                  )}
                  <div className="flex h-36 items-center justify-center bg-white overflow-hidden">
                    {b.logoUrl ? (
                      <img
                        src={b.logoUrl}
                        alt={b.name}
                        className="w-full h-full object-cover p-2"
                        style={{ maxHeight: "128px" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <Briefcase className="w-12 h-12 text-gray-300" />
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <h3 className="mb-1 text-base font-bold text-gray-900 line-clamp-1">
                      {b.name}
                    </h3>
                    {b.sektor && (
                      <p className="text-[11px] text-gray-500 uppercase tracking-wide line-clamp-1">
                        {b.sektor}
                      </p>
                    )}
                    {b.sehir && (
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {b.sehir}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {/* Placeholder kartlar — 4'ü doldurmak için */}
              {[...Array(Math.max(0, 4 - businesses.length))].map((_, i) => (
                <div
                  key={`placeholder-${i}`}
                  className="h-48 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 animate-pulse opacity-40"
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeaturedBusinesses;
